"use client"
import React, { useState, useEffect, useMemo } from "react";

import moment from "moment";
import { CalendarDate, EmptyCalendarCell } from "@/components/shared/calendar/CalendarDate";
import { useMediaQuery } from "react-responsive";
import "@/components/shared/calendar/CalendarWidget.css";
import UralityImage from "@/components/shared/UralityImage";
import { Ratio } from "react-bootstrap";

export function CalendarWidget({ className = "", events = [] }) {
  const [currentDate, setCurrentDate] = useState(moment());
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [hoveredCellIndex, setHoveredCellIndex] = useState(null);
  const [mobilePopoverEvent, setMobilePopoverEvent] = useState(null);
  const [calendarHeaders, setCalendarHeaders] = useState([
    "SUN",
    "MON",
    "TUE",
    "WED",
    "THU",
    "FRI",
    "SAT"
  ]);

  const isSm = useMediaQuery({ query: "(max-width: 767.98px)" });

  useEffect(() => {
    if (isSm) {
      setCalendarHeaders(["SU", "MO", "TU", "WE", "TH", "FR", "SA"]);
    }
  }, [isSm]);

  // Generate calendar grid
  const calendarData = useMemo(() => {
    const today = moment();
    const firstDay = currentDate
      .clone()
      .startOf("month")
      .startOf("week");
    const lastDay = currentDate
      .clone()
      .endOf("month")
      .endOf("week");

    const days = [];

    let day = firstDay.clone();

    while (day.isSameOrBefore(lastDay, "day")) {
      const dateStr = day.format("YYYY-MM-DD");
      const event = events.find(e => e.date?.startsWith(dateStr));
      const isCurrentMonth = day.month() === currentDate.month();

      days.push({
        date: day.clone(),
        event,
        isCurrentMonth,
        isToday: day.isSame(today, "day")
      });

      day.add(1, "day");
    }

    return days;
  }, [currentDate, events]);

  const handlePreviousMonth = () => {
    setCurrentDate(prev => prev.clone().subtract(1, "month"));
    setHoveredEvent(null);
    setHoveredCellIndex(null);
    setMobilePopoverEvent(null);
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => prev.clone().add(1, "month"));
    setHoveredEvent(null);
    setHoveredCellIndex(null);
    setMobilePopoverEvent(null);
  };

  const handleEventHover = (event, index) => {
    if (!isSm) {
      // const row = Math.floor(index / 7);
      // const col = index % 7;
      // console.log(
      //   `Hovering ${
      //     event.title
      //   } at index ${index} (row ${row}, col ${col}), totalRows: ${Math.ceil(
      //     calendarData.length / 7
      //   )}`
      // );
      setHoveredEvent(event);
      setHoveredCellIndex(event ? index : null);
    }
  };

  const handleEventLeave = () => {
    // Don't clear immediately - let the next hover or overlay leave handle it
  };

  const handleOverlayLeave = () => {
    setHoveredEvent(null);
    setHoveredCellIndex(null);
  };

  const handleEventClick = event => {
    if (isSm) {
      setMobilePopoverEvent(event);
    }
  };

  const handleCloseMobilePopover = () => {
    setMobilePopoverEvent(null);
  };

  // Get the anchor cell for the 2x3 overlay that doesn't cover the hovered cell
  // Returns { anchorIndex, anchorBottom } where anchorBottom indicates if overlay should be bottom-anchored
  const getAnchorCell = hoveredIndex => {
    const row = Math.floor(hoveredIndex / 7);
    const col = hoveredIndex % 7;
    const totalRows = Math.ceil(calendarData.length / 7);

    // Helper function to check if a 2x3 overlay starting at anchor (top-anchored) would cover the hovered cell
    const wouldCoverHoveredCellTop = (anchorRow, anchorCol) => {
      const coveredIndices = [
        anchorRow * 7 + anchorCol,
        anchorRow * 7 + (anchorCol + 1),
        (anchorRow + 1) * 7 + anchorCol,
        (anchorRow + 1) * 7 + (anchorCol + 1),
        (anchorRow + 2) * 7 + anchorCol,
        (anchorRow + 2) * 7 + (anchorCol + 1)
      ];
      return coveredIndices.includes(hoveredIndex);
    };

    // Helper function to check if a 2x3 overlay ending at anchor (bottom-anchored) would cover the hovered cell
    const wouldCoverHoveredCellBottom = (anchorRow, anchorCol) => {
      const coveredIndices = [
        (anchorRow - 2) * 7 + anchorCol,
        (anchorRow - 2) * 7 + (anchorCol + 1),
        (anchorRow - 1) * 7 + anchorCol,
        (anchorRow - 1) * 7 + (anchorCol + 1),
        anchorRow * 7 + anchorCol,
        anchorRow * 7 + (anchorCol + 1)
      ];
      return coveredIndices.includes(hoveredIndex);
    };

    // First, try top-anchored positions (overlay extends downward)
    // Only try these if there's enough space below

    // Option 1: Top-left of hovered cell
    if (row >= 1 && col >= 1) {
      const anchorRow = row - 1;
      const anchorCol = col - 1;
      if (
        anchorRow >= 0 &&
        anchorRow <= totalRows - 3 &&
        anchorCol >= 0 &&
        anchorCol <= 5 &&
        !wouldCoverHoveredCellTop(anchorRow, anchorCol)
      ) {
        return { anchorIndex: anchorRow * 7 + anchorCol, anchorBottom: false };
      }
    }

    // Option 2: Top-right of hovered cell
    if (row >= 1 && col <= 5) {
      const anchorRow = row - 1;
      const anchorCol = col;
      if (
        anchorRow >= 0 &&
        anchorRow <= totalRows - 3 &&
        anchorCol >= 0 &&
        anchorCol <= 5 &&
        !wouldCoverHoveredCellTop(anchorRow, anchorCol)
      ) {
        return { anchorIndex: anchorRow * 7 + anchorCol, anchorBottom: false };
      }
    }

    // Option 3: Left of hovered cell (2 columns left)
    if (col >= 2) {
      const anchorRow = row;
      const anchorCol = col - 2;
      if (
        anchorRow >= 0 &&
        anchorRow <= totalRows - 3 &&
        anchorCol >= 0 &&
        anchorCol <= 5 &&
        !wouldCoverHoveredCellTop(anchorRow, anchorCol)
      ) {
        return { anchorIndex: anchorRow * 7 + anchorCol, anchorBottom: false };
      }
    }

    // Option 4: Right of hovered cell
    if (col <= 4) {
      const anchorRow = row;
      const anchorCol = col + 1;
      if (
        anchorRow >= 0 &&
        anchorRow <= totalRows - 3 &&
        anchorCol >= 0 &&
        anchorCol <= 5 &&
        !wouldCoverHoveredCellTop(anchorRow, anchorCol)
      ) {
        return { anchorIndex: anchorRow * 7 + anchorCol, anchorBottom: false };
      }
    }

    // Option 5: Above-left (for bottom row events)
    if (row >= 2 && col >= 1) {
      const anchorRow = row - 2;
      const anchorCol = col - 1;
      if (
        anchorRow >= 0 &&
        anchorRow <= totalRows - 3 &&
        anchorCol >= 0 &&
        anchorCol <= 5 &&
        !wouldCoverHoveredCellTop(anchorRow, anchorCol)
      ) {
        return { anchorIndex: anchorRow * 7 + anchorCol, anchorBottom: false };
      }
    }

    // Option 6: Above-right (for bottom row events)
    if (row >= 2 && col <= 5) {
      const anchorRow = row - 2;
      const anchorCol = col;
      if (
        anchorRow >= 0 &&
        anchorRow <= totalRows - 3 &&
        anchorCol >= 0 &&
        anchorCol <= 5 &&
        !wouldCoverHoveredCellTop(anchorRow, anchorCol)
      ) {
        return { anchorIndex: anchorRow * 7 + anchorCol, anchorBottom: false };
      }
    }

    // If no top-anchored position works, try bottom-anchored positions (overlay extends upward)
    // Bottom-anchored: anchor is at the bottom row of the 2x3 overlay
    // For bottom-anchored, if anchor is at row N, overlay covers rows N-2, N-1, N

    // Option 7: Left of hovered cell, bottom-anchored at hovered row
    if (row >= 2 && col >= 1) {
      const anchorRow = row;
      const anchorCol = col - 1;
      if (
        anchorCol >= 0 &&
        anchorCol <= 5 &&
        !wouldCoverHoveredCellBottom(anchorRow, anchorCol)
      ) {
        return { anchorIndex: anchorRow * 7 + anchorCol, anchorBottom: true };
      }
    }

    // Option 8: Right of hovered cell, bottom-anchored at hovered row
    if (row >= 2 && col <= 5) {
      const anchorRow = row;
      const anchorCol = col + 1;
      if (
        anchorCol >= 0 &&
        anchorCol <= 5 &&
        !wouldCoverHoveredCellBottom(anchorRow, anchorCol)
      ) {
        return { anchorIndex: anchorRow * 7 + anchorCol, anchorBottom: true };
      }
    }

    // Option 9: Two columns left of hovered cell, bottom-anchored at hovered row
    if (row >= 2 && col >= 2) {
      const anchorRow = row;
      const anchorCol = col - 2;
      if (
        anchorCol >= 0 &&
        anchorCol <= 5 &&
        !wouldCoverHoveredCellBottom(anchorRow, anchorCol)
      ) {
        return { anchorIndex: anchorRow * 7 + anchorCol, anchorBottom: true };
      }
    }

    // Option 10: Two columns right of hovered cell, bottom-anchored at hovered row
    if (row >= 2 && col <= 4) {
      const anchorRow = row;
      const anchorCol = col + 2;
      if (
        anchorCol >= 0 &&
        anchorCol <= 5 &&
        !wouldCoverHoveredCellBottom(anchorRow, anchorCol)
      ) {
        return { anchorIndex: anchorRow * 7 + anchorCol, anchorBottom: true };
      }
    }

    // Option 11: One row below, left of hovered cell, bottom-anchored
    if (row < totalRows - 1 && row >= 2 && col >= 1) {
      const anchorRow = row + 1;
      const anchorCol = col - 1;
      if (
        anchorRow < totalRows &&
        anchorCol >= 0 &&
        anchorCol <= 5 &&
        !wouldCoverHoveredCellBottom(anchorRow, anchorCol)
      ) {
        return { anchorIndex: anchorRow * 7 + anchorCol, anchorBottom: true };
      }
    }

    // Option 12: One row below, right of hovered cell, bottom-anchored
    if (row < totalRows - 1 && row >= 2 && col <= 5) {
      const anchorRow = row + 1;
      const anchorCol = col + 1;
      if (
        anchorRow < totalRows &&
        anchorCol >= 0 &&
        anchorCol <= 5 &&
        !wouldCoverHoveredCellBottom(anchorRow, anchorCol)
      ) {
        return { anchorIndex: anchorRow * 7 + anchorCol, anchorBottom: true };
      }
    }

    return null;
  };

  const hoverContent = hoveredEvent => {
    let className = isSm
      ? "calendar-widget-mobile-popover-"
      : "calendar-widget-overlay-";
    return (
      <>
        <h3 className={`${className}title`}>{hoveredEvent.title}</h3>
        {hoveredEvent.subtitle && (
          <h5 className={`${className}subtitle`}>{hoveredEvent.subtitle}</h5>
        )}
        <h6 className={`${className}date`}>
          {hoveredEvent.multiDay ? (
            <>
              {moment(hoveredEvent.startDate).format("MMMM D")} -{" "}
              {moment(hoveredEvent.endDate).format("MMMM D, YYYY")}
            </>
          ) : (
            <>{moment(hoveredEvent.startDate).format("dddd, MMMM D, YYYY")}</>
          )}
        </h6>
        {!hoveredEvent.multiDay && (
          <h6 className={`${className}time`}>
            {moment(hoveredEvent.startDate).format("h:mm a")} -{" "}
            {moment(hoveredEvent.endDate).format("h:mm a")}
          </h6>
        )}
        {hoveredEvent.location && (
          <p className={`${className}location`}>{hoveredEvent.location}</p>
        )}
        <p className={`${className}description`}>
          {hoveredEvent.description && hoveredEvent.description !== ""
            ? hoveredEvent.description
            : "No description provided."}
          {hoveredEvent.tags && hoveredEvent.tags.length > 0 && (
              <div
                className={`tags ${className}tags`}
                style={{ margin: "0.75rem 0" }}
              >
                {hoveredEvent.tags.map(tag => (
                  <span key={tag} className="tag">
                    {tag}
                  </span>
                ))}
              </div>
          )}
        </p>
        {hoveredEvent.callToActions && hoveredEvent.callToActions.length > 0 && (
          <div className={`${className}ctas`}>
            {hoveredEvent.callToActions.map((cta, idx) => (
              <a
                href={cta.url}
                rel="noopener noreferrer"
                target="_blank"
                className={idx === 0 ? "btn btn-sm btn-primary" : "a"}
              >
                {cta.label}
              </a>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className={`calendar-widget-container calendar-widget ${className}`}>
      {/* Calendar Header */}
      <div className="calendar-widget-header-container">
        <div className="calendar-widget-header">
          <div
            onClick={handlePreviousMonth}
            className="calendar-widget-header-button clickable"
          >
            <i className="fa-solid fa-chevron-left"></i>
            {/* <ChevronLeft className="w-6 h-6 text-gray-600" /> */}
          </div>
          <h1 className="calendar-widget-header-title">
            {currentDate.format("MMMM YYYY")}
          </h1>
          <div
            onClick={handleNextMonth}
            className="calendar-widget-header-button clickable"
          >
            <i className="fa-solid fa-chevron-right"></i>
            {/* <ChevronRight className="w-6 h-6 text-gray-600" /> */}
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-widget-grid-container">
        {/* Day Headers */}
        <div className="calendar-widget-day-headers">
          {calendarHeaders.map(day => (
            <div key={day} className="calendar-widget-day-header">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Body */}
        <div className="calendar-widget-grid">
          {calendarData.map(
            ({ date, event, isCurrentMonth, isToday }, index) => {
              const anchorData =
                hoveredCellIndex !== null
                  ? getAnchorCell(hoveredCellIndex)
                  : null;
              const isAnchorCell =
                anchorData && anchorData.anchorIndex === index;
              const anchorBottom = anchorData ? anchorData.anchorBottom : false;

              return (
                <div key={index} className="calendar-widget-cell">
                  {event ? (
                    <CalendarDate
                      event={event}
                      isSm={isSm}
                      isCurrentMonth={isCurrentMonth}
                      isToday={isToday}
                      dayNumber={date.date()}
                      onHover={() => handleEventHover(event, index)}
                      onLeave={handleEventLeave}
                      onClick={handleEventClick}
                    />
                  ) : (
                    <EmptyCalendarCell
                      dayNumber={date.date()}
                      isCurrentMonth={isCurrentMonth}
                      isToday={isToday}
                      isSm={isSm}
                      onHover={() => handleEventHover(null, index)}
                    />
                  )}

                  {/* Desktop 2x3 Hover Overlay - appears at the anchor cell and spans 2x3 */}
                  {!isSm && hoveredEvent && isAnchorCell && (
                    <div
                      className={`calendar-widget-overlay ${
                        anchorBottom ? "calendar-widget-overlay-bottom" : ""
                      }`}
                      onMouseEnter={() => {
                        // Keep the overlay visible when hovering over it
                        setHoveredEvent(hoveredEvent);
                        setHoveredCellIndex(hoveredCellIndex);
                      }}
                      onMouseLeave={handleOverlayLeave}
                    >
                      <div className="calendar-widget-overlay-content">
                        {hoverContent(hoveredEvent)}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
          )}
        </div>
      </div>

      {/* Mobile Popover */}
      {isSm && mobilePopoverEvent && (
        <div className="calendar-widget-mobile-popover">
          <div className="calendar-widget-mobile-popover-content">
            <div className="calendar-widget-mobile-popover-inner">
              <button
                onClick={handleCloseMobilePopover}
                className="btn btn-secondary circle calendar-widget-mobile-popover-close"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
              {mobilePopoverEvent.coverPhoto && (
                <Ratio aspectRatio="16x9" style={{ marginBottom: "1rem" }}>
                  <UralityImage
                    imageId={mobilePopoverEvent.coverPhoto}
                    size="md"
                  />
                </Ratio>
              )}
              {hoverContent(mobilePopoverEvent)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
